<template>
  <f7-page name="community-growth">
    <f7-navbar :title="$t('growth.dashboard.title')" back-link />
  
    <f7-block class="pb-0 growth-page space-y-4 max-w-lg mx-auto no-margin no-padding">
      <f7-block v-if="loading" class="loading-block">
        <f7-preloader color="green" />
      </f7-block>

      <template v-else>
        <!-- avocash Vitality Ring Card -->
        <div class="vitality-card">
          <div class="vitality-ambient"></div>
          <div class="vitality-head">
            <div class="vitality-id">
              <div class="vitality-ring">
                <svg viewBox="0 0 64 64">
                  <circle class="ring-track" cx="32" cy="32" fill="transparent" r="26" stroke="currentColor" stroke-width="5"></circle>
                  <circle class="ring-value" cx="32" cy="32" fill="transparent" r="26" stroke="currentColor" stroke-dasharray="163.36" stroke-dashoffset="21.23" stroke-linecap="round" stroke-width="5.5"></circle>
                </svg>
                <div class="vitality-center">
                  <span class="vitality-pct">87<span class="pct-suffix">%</span></span>
                </div>
              </div>
              <div>
                <div class="vitality-name-row">
                  <span class="vitality-name">{{ $t('growth.dashboard.vitalityTitle') }}</span>
                  <span class="vitality-chip">{{ $t('growth.dashboard.vitalityStatus') }}</span>
                </div>
                <p class="vitality-desc">{{ $t('growth.dashboard.vitalityDesc') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Wallet Card -->
        <f7-card class="growth-card mb-3">
          <f7-card-header>
            <f7-icon f7="wallet" size="24" class="t-green" />
            <div class="t-bold">{{ $t('growth.dashboard.communityWallet') }}</div>
          </f7-card-header>
          <f7-card-content>
            <div class="wallet-stats">
              <div class="wallet-stat">
                <div class="t-muted t-small">{{ $t('growth.dashboard.available') }}</div>
                <div class="t-h3 t-green t-bold">{{ wallet.availableCredit }} <small>{{wallet.currency}}</small></div>
              </div>
              <div class="wallet-stat">
                <div class="t-muted t-small">{{ $t('growth.dashboard.locked') }}</div>
                <div class="t-h4 t-bold">{{ wallet.lockedCredit }} <small>{{wallet.currency}}</small></div>
              </div>
              <div class="wallet-stat">
                <div class="t-muted t-small">{{ $t('growth.dashboard.earned') }}</div>
                <div class="t-h5 t-gold t-bold">{{ wallet.earnedCredit }} <small>{{wallet.currency}}</small></div>
              </div>
            </div>
          </f7-card-content>
          <f7-card-footer>
            <f7-button small fill class="btn-green" :href="links.spendCredits">{{ $t('growth.dashboard.spendCredits') }}</f7-button>
          </f7-card-footer>
        </f7-card>

        <!-- Trust Level Card -->
        <f7-card class="growth-card mb-3">
          <f7-card-header>
            <f7-icon f7="star_fill" size="24" class="t-gold" />
            <div class="t-bold">{{ $t('growth.dashboard.trustLevel') }}</div>
          </f7-card-header>
          <f7-card-content>
            <div class="text-align-center">
              <div class="trust-ring" :style="{ borderColor: trustMeta.color }">
                <f7-icon :f7="trustMeta.icon" size="30" :style="{ color: trustMeta.color }" />
              </div>
              <div class="t-h3 t-bold trust-label" :style="{ color: trustMeta.color }">
                {{ $t(trustMeta.label) }}
              </div>
              <div class="t-muted t-small">{{ $t('growth.dashboard.trustPoints') }} {{ trust.trustPoints }}</div>
              <f7-progressbar
                :progress="trustPct"
                class="progress-gold mt-2"
              ></f7-progressbar>
              <div v-if="nextTrustLabel" class="t-xs t-muted mt-1">{{ $t('growth.dashboard.pctToNextLevel', { pct: trustPct, level: nextTrustLabel }) }}</div>
            </div>
          </f7-card-content>
        </f7-card>

        <!-- Milestones Card -->
        <f7-card class="growth-card mb-3">
          <f7-card-header>
            <f7-icon f7="flag_fill" size="24" class="t-blue" />
            <div class="t-bold">{{ $t('growth.dashboard.milestones') }}</div>
          </f7-card-header>
          <f7-card-content>
            <f7-list media-list v-if="milestones.length" class="growth-list">
              <f7-list-item
                v-for="m in milestones"
                :key="m.id"
                :title="m.name"
                :subtitle="m.description"
                :footer="m.completed ? $t('growth.dashboard.completed') : $t('growth.dashboard.inProgress')"
                :checkbox="m.completed"
                :checked="m.completed"
              ></f7-list-item>
            </f7-list>
            <div v-else class="t-muted text-align-center py-3">{{ $t('growth.dashboard.noMilestones') }}</div>
          </f7-card-content>
        </f7-card>

        <!-- Achievements Card -->
        <f7-card class="growth-card mb-3">
          <f7-card-header>
            <f7-icon f7="rosette" size="24" class="t-purple" />
            <div class="t-bold">{{ $t('growth.dashboard.achievements') }}</div>
          </f7-card-header>
          <f7-card-content>
            <div v-if="achievements.length === 0" class="t-muted text-align-center py-3">
              {{ $t('growth.dashboard.noAchievements') }}
            </div>
            <div v-else class="badge-row">
              <f7-chip
                v-for="a in achievements"
                :key="a.id"
                :text="a.badge"
                outline
                class="badge-chip badge-purple"
              >
                <f7-icon f7="star" slot="icon" class="t-purple"></f7-icon>
              </f7-chip>
            </div>
          </f7-card-content>
          <f7-card-footer>
            <f7-button small fill round raised class="btn-purple" :href="links.achievements">{{ $t('growth.dashboard.viewAll') }}</f7-button>
          </f7-card-footer>
        </f7-card>

        <!-- Referrals Card -->
        <f7-card class="growth-card mb-3">
          <f7-card-header>
            <f7-icon f7="people" size="24" class="t-leaf" />
            <div class="t-bold">{{ $t('growth.dashboard.communityReferrals') }}</div>
          </f7-card-header>
          <f7-card-content>
            <f7-list media-list v-if="referrals.length" class="growth-list">
              <f7-list-item
                v-for="r in referrals"
                :key="r.id"
                :title="r.invitedCommunityName"
                :subtitle="$t('growth.dashboard.progress', { progress: r.activationProgress })"
                :footer="r.status"
              >
                <template #after>
                  <f7-progressbar
                    :progress="r.activationProgress"
                    class="progress-leaf"
                    style="width: 100px"
                  ></f7-progressbar>
                </template>
              </f7-list-item>
            </f7-list>
            <div v-else class="t-muted text-align-center py-3">{{ $t('growth.dashboard.noReferrals') }}</div>
          </f7-card-content>
        </f7-card>

        <!-- Recent Rewards Timeline -->
        <f7-card class="growth-card mb-3">
          <f7-card-header>
            <f7-icon f7="bell" size="24" class="t-terracotta" />
            <div class="t-bold">{{ $t('growth.dashboard.recentRewards') }}</div>
          </f7-card-header>
          <f7-card-content>
            <div v-if="notifications.length" class="reward-list">
              <div v-for="n in notifications" :key="n.id" class="reward-item">
                <div class="reward-icon t-gold">
                  <f7-icon f7="gift" size="20"></f7-icon>
                </div>
                <div class="reward-body">
                  <div class="t-bold t-small">{{ n.title }}</div>
                  <div class="t-xs t-muted">{{ n.message }}</div>
                  <div class="t-xs t-muted">{{ formatDate(n.date) }}</div>
                </div>
              </div>
            </div>
            <div v-else class="t-muted text-align-center py-3">{{ $t('growth.dashboard.noRewards') }}</div>
          </f7-card-content>
        </f7-card>
      </template>
    </f7-block>

    <f7-block class="pt-0 max-w-lg mx-auto no-margin no-padding">
      <f7-button large fill raised round class="btn-green" :href="links.spendCredits" block>{{ $t('growth.dashboard.spendCredits') }}</f7-button>
    </f7-block>
  </f7-page>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import {
  f7Page, f7Navbar, f7Block, f7Card, f7CardHeader, f7CardContent, f7CardFooter,
  f7Icon, f7List, f7ListItem, f7Progressbar, f7Chip, f7Button, f7Preloader
} from 'framework7-vue'
import { useGrowthStore } from '../js/pinia-stores/growth.js'
import { trustMeta as trustMetaOf, trustProgress, nextTrustLevel } from '../constants/growth-levels.js'
import i18n from '../js/i18n.js'
const { t } = i18n.global

const props = defineProps({ groupId: { type: String, default: '' } })

const growthStore = useGrowthStore()

const groupId = computed(() => props.groupId || '123')
const wallet = computed(() => growthStore.wallet)
const trust = computed(() => growthStore.trust)
const milestones = computed(() => growthStore.milestones)
const achievements = computed(() => growthStore.achievements)
const referrals = computed(() => growthStore.referrals)
const notifications = computed(() => growthStore.notifications)
const loading = computed(() => growthStore.loading)

const trustMeta = computed(() => trustMetaOf(trust.value.trustLevel))
const trustPct = computed(() =>
  trustProgress(trust.value.trustPoints, trust.value.trustLevel))
const nextTrustLabel = computed(() => {
  const code = nextTrustLevel(trust.value.trustLevel)
  return code ? t(`growth.trustLevel.${code.toLowerCase()}`) : ''
})

const links = computed(() => {
  const g = props.groupId
  return {
    spendCredits: g ? `/groups/${g}/growth/spend-credits/` : '/spend-credits/',
    achievements: g ? `/groups/${g}/growth/achievements/` : '/achievements/',
    health: g ? `/groups/${g}/growth/health/` : '/community-health/',
  }
})

onMounted(async () => {
  try {
    await growthStore.fetchDashboard(groupId.value)
  } catch (e) {
    console.warn('Growth dashboard unavailable:', e)
  }
})

function formatDate(dateString) {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}
</script>

<style scoped>
.growth-page {
  --avo-green: var(--f7-theme-color, #1a6b42);
  --avo-green-light: var(--avocash-green-light, #eaf2eb);
  --avo-gold: var(--f7-secondary, #c9a84c);
  --avo-gold-light: var(--avocash-gold-light, #fdf6e3);
  --avo-blue: var(--f7-blue, #3b4fca);
  --avo-blue-light: var(--avocash-blue-light, #ebf0fc);
  --avo-terracotta: var(--f7-error, #ba1a1a);
  --avo-terracotta-light: var(--avocash-terracotta-light, #fdecea);
  --avo-leaf: var(--f7-theme-color, #5f8a4a);
  --avo-purple: var(--f7-purple, #6b46c1);
  --avo-purple-light: var(--avocash-purple-light, #f5f0ff);
  --avo-surface: var(--avocash-surface, #f4f7f2);
  --avo-pit: var(--f7-on-surface, #1c1c1e);
}

/* ── Vitality Ring hero (all inks/surfaces theme-flipping) ── */
.vitality-card {
  position: relative;
  overflow: hidden;
  padding: 20px;
  border-radius: 16px;
  background: var(--f7-card-bg-color, #fff);
  border: 1px solid var(--f7-list-item-border-color, rgba(0, 0, 0, 0.05));
  box-shadow: 0 2px 8px -2px rgba(26, 107, 66, 0.06), 0 1px 3px rgba(44, 52, 46, 0.06);
  margin-bottom: 12px;
}
.vitality-ambient {
  position: absolute;
  right: -48px;
  top: -48px;
  width: 144px;
  height: 144px;
  background: color-mix(in srgb, var(--f7-theme-color) 18%, transparent);
  border-radius: 50%;
  filter: blur(32px);
  pointer-events: none;
}
.vitality-head {
  position: relative;
  z-index: 1;
}
.vitality-id {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.vitality-ring {
  position: relative;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
}
.vitality-ring svg {
  width: 64px;
  height: 64px;
  transform: rotate(-90deg);
}
.ring-track {
  color: var(--f7-surface-container-highest, #dce5dc);
}
.ring-value {
  color: var(--f7-theme-color, #1a6b42);
}
.vitality-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.vitality-pct {
  font-size: 18px;
  font-weight: 700;
  color: var(--f7-theme-color, #1a6b42);
}
.pct-suffix {
  font-size: 10px;
  font-weight: 600;
}
.vitality-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.vitality-name {
  font-size: 16px;
  font-weight: 700;
  color: var(--f7-on-surface, #161d18);
}
.vitality-chip {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--f7-theme-color) 12%, var(--f7-card-bg-color, #fff));
  color: var(--f7-theme-color, #1a6b42);
}
.vitality-desc {
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: var(--f7-on-surface-variant, #3f4941);
}

.loading-block { text-align: center; padding: 40px 0; }

.t-bold { font-weight: 600; }
.t-h3 { font-size: 22px; font-weight: 700; line-height: 1.2; }
.t-h4 { font-size: 18px; font-weight: 600; }
.t-h5 { font-size: 15px; font-weight: 600; }
.t-small { font-size: 12px; }
.t-xs { font-size: 11px; }
.t-muted { color: var(--f7-on-surface-variant, #6f7a71); }

.t-green { color: var(--avo-green); }
.t-gold { color: var(--avo-gold); }
.t-blue { color: var(--avo-blue); }
.t-leaf { color: var(--avo-leaf); }
.t-purple { color: var(--avo-purple); }
.t-terracotta { color: var(--avo-terracotta); }

.growth-card { border-radius: 14px; }
.growth-card :deep(.card-header) { font-size: 15px; gap: 8px; }
.growth-card :deep(.card-footer) { padding-top: 10px; }

.wallet-stats { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.wallet-stat { text-align: left; }

.trust-ring {
  width: 64px; height: 64px; margin: 0 auto 8px;
  border-radius: 50%; border: 3px solid var(--avo-gold);
  display: flex; align-items: center; justify-content: center;
  background: var(--avo-gold-light);
}
.trust-label { text-transform: capitalize; }

.progress-gold :deep(.progressbar-inner) { background: var(--avo-gold); }
.progress-leaf :deep(.progressbar-inner) { background: var(--avo-leaf); }

.growth-list :deep(.item-title) { font-size: 14px; }
.growth-list :deep(.item-subtitle) { font-size: 12px; }

.badge-row { display: flex; flex-wrap: wrap; justify-content: center; gap: 6px; }
.badge-chip { margin: 0; }
.badge-purple :deep(.chip-label) { color: var(--avo-purple); }
.badge-purple :deep(.chip-outline) { border-color: var(--avo-purple); }

.btn-green { background: var(--avo-green); color: #fff; }
.btn-purple { background: var(--avo-purple); color: #fff; }
.btn-green.active-state, .btn-purple.active-state { opacity: .8; }

.reward-list { display: flex; flex-direction: column; gap: 12px; }
.reward-item { display: flex; gap: 10px; align-items: flex-start; }
.reward-icon {
  flex: 0 0 36px; width: 36px; height: 36px;
  border-radius: 50%; background: var(--avo-gold-light);
  display: flex; align-items: center; justify-content: center;
}
.reward-body { min-width: 0; }
.reward-body .t-bold { font-size: 13px; }

.stat-desc {
  font-size: 12px;
}
.pct-suffix {
  font-size: 10px;
}
</style>
